import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { useSocket } from './SocketContext';
import { useToast } from './ToastContext';
import { playEndTone, playRingback, playRingtone, stopCallTone, unlockCallAudio } from '../utils/callTones';
import { triggerHaptic } from '../utils/nativeBridge';
import {
  getCallAudioState, onCallAudioChange, setCallAudioDevice, startCallAudio, stopCallAudio,
} from '../utils/audioRoute';
import {
  NativePeerConnection, onNativeRemoteVideo, requestNativeCamera, requestNativeMicrophone,
  setNativeCameraEnabled, setNativeMuted, startNativeVideo, switchNativeCamera, usesNativeCallMedia,
} from '../utils/nativePeer';
import { SfuSession } from '../utils/sfuClient';
import {
  answerCallKitCall, endCallKitCall, onCallKitEvent, reportCallConnected, reportCallConnecting,
  reportIncomingCall, setCallKitMuted, startCallKitCall, usesCallKit,
} from '../utils/callKit';
import {
  optimizeAudioSdp, prioritizeAudioSender, createCallQualityMonitor,
} from '../utils/callResilience';

// Keep call alive across temporary packet loss, tunnel transit, or network handover
const RECONNECT_GRACE_MS = 30000;
const RESTART_ICE_DEBOUNCE_MS = 2500;

// STUN alone fails behind symmetric NATs (common on mobile carriers); configure a
// TURN server via env for reliable connectivity in production.
const buildRtcConfig = () => {
  const iceServers = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ];
  if (process.env.REACT_APP_TURN_URL) {
    iceServers.push({
      urls: process.env.REACT_APP_TURN_URL.split(','),
      username: process.env.REACT_APP_TURN_USERNAME,
      credential: process.env.REACT_APP_TURN_CREDENTIAL,
    });
  }
  return {
    iceServers,
    iceCandidatePoolSize: 2,
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
  };
};

// Advertised to the server; the SFU is used only when both callers support it
const CALL_CAPS = ['sfu'];

const AUDIO_CONSTRAINTS = {
  echoCancellation: { ideal: true },
  noiseSuppression: { ideal: true },
  autoGainControl: { ideal: true },
  // Request native hardware DSP echo cancellation where available (Android/macOS/Chrome)
  echoCancellationType: { ideal: 'system' },
  googEchoCancellation: true,
  googExperimentalEchoCancellation: true,
  googAutoGainControl: true,
  googExperimentalAutoGainControl: true,
  googNoiseSuppression: true,
  googExperimentalNoiseSuppression: true,
  googHighpassFilter: true, // Strips low-frequency rumble that causes AEC filter drift
  googTypingNoiseDetection: true,
  googAudioMirroring: false,
};
// 720p is plenty for a phone screen and stays light on cellular
const videoConstraints = (facing) => ({
  facingMode: facing,
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
});

const getCameraTrack = async (facing) => {
  const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints(facing) });
  return stream.getVideoTracks()[0];
};

// iOS: native WebRTC so call audio uses the earpiece (see utils/nativePeer.js).
// Native captures the mic and camera itself; on the web we add the
// getUserMedia tracks.
const newPeer = (rtcConfig, localStream, video) => {
  if (usesNativeCallMedia()) return new NativePeerConnection({ ...rtcConfig, video });
  const pc = new RTCPeerConnection(rtcConfig);
  localStream?.getTracks().forEach((track) => pc.addTrack(track, localStream));
  return pc;
};

const END_SCREEN_MS = 1800;

const END_REASON_LABELS = {
  declined: 'Call declined',
  busy: 'User is busy',
  no_answer: 'No answer',
  unavailable: 'Unavailable right now',
  failed: 'Call failed',
  mic_denied: 'Microphone access denied',
};

// What the iPhone's call log records when the server ends a call
const callKitEndReason = (reason, status) => {
  if (reason === 'answered_elsewhere') return 'answeredElsewhere';
  if (status === 'incoming') return reason === 'declined' ? 'declinedElsewhere' : 'unanswered';
  return 'remoteEnded';
};

const callKitHandle = (peer) => {
  if (peer?.handle) return `@${peer.handle.replace(/^@+/, '')}`;
  if (peer?.id) return String(peer.id);
  return peer?.username || '';
};

// video: the call was placed as a video call. media: 'sfu' | 'p2p' once connecting.
// ckId: the call's id in iOS CallKit (see utils/callKit.js), null elsewhere.
const IDLE = {
  status: 'idle',
  callId: null,
  ckId: null,
  peer: null,
  startedAt: null,
  endLabel: null,
  video: false,
  media: null,
  reconnecting: false,
  networkQuality: 'good',
};
const NO_AUDIO_ROUTES = { devices: [], current: null };
const CAMERA_OFF = { on: false, facing: 'user' };

const CallContext = createContext(null);

export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
  const socket = useSocket();
  const toast = useToast();

  // status: idle | outgoing | incoming | connecting | active | ended
  const [call, setCall] = useState(IDLE);
  const [muted, setMutedState] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  // Native audio outputs (earpiece / speaker / headsets); empty on the web
  const [audioRoute, setAudioRoute] = useState(NO_AUDIO_ROUTES);

  // Video. On iOS the frames never reach JS: native renders them and we only
  // track whether there is something to show.
  const [camera, setCameraState] = useState(CAMERA_OFF);
  const [localVideoStream, setLocalVideoStream] = useState(null); // web preview
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null); // web
  const [nativeRemoteVideo, setNativeRemoteVideo] = useState(false); // iOS
  // The other side's camera switch, relayed by the server
  const [remoteCameraOn, setRemoteCameraOn] = useState(false);

  const callRef = useRef(IDLE);
  const mutedRef = useRef(false);
  // CallKit answered a call the socket hadn't announced yet (app woken by a push)
  const pendingCallKitAnswerRef = useRef(null);
  // Latest call actions, for handlers registered once
  const acceptCallRef = useRef(null);
  const declineCallRef = useRef(null);
  const hangUpRef = useRef(null);
  const startCallRef = useRef(null);
  const socketRef = useRef(socket);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  const waitForSocket = useCallback((maxWaitMs = 6000) => {
    return new Promise((resolve) => {
      if (socketRef.current?.connected) return resolve(socketRef.current);
      const start = Date.now();
      const interval = setInterval(() => {
        if (socketRef.current?.connected) {
          clearInterval(interval);
          resolve(socketRef.current);
        } else if (Date.now() - start > maxWaitMs) {
          clearInterval(interval);
          resolve(socketRef.current?.connected ? socketRef.current : null);
        }
      }, 100);
    });
  }, []);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const cameraRef = useRef(CAMERA_OFF);
  // Our outgoing video sender (web), kept across replaceTrack(null)
  const videoSenderRef = useRef(null);
  // iOS: whether the native peer has a camera track yet
  const nativeVideoRef = useRef(false);
  const pendingCandidatesRef = useRef([]);
  // SFU mode: the media server session (null for peer-to-peer calls)
  const sfuRef = useRef(null);
  // P2P signals that arrived before our peer connection existed
  const earlySignalsRef = useRef([]);
  // Socket dropped mid-call; re-claim the call once it reconnects
  const resumeOnConnectRef = useRef(false);
  const hangupBeforeAckRef = useRef(false);
  const endTimerRef = useRef(null);
  // Output the user just picked; native reports can lag behind the switch
  const pendingAudioIdRef = useRef(null);

  // Resilience & Reconnection refs
  const reconnectTimerRef = useRef(null);
  const iceRestartDebounceTimerRef = useRef(null);
  const iceRestartingRef = useRef(false);
  const isCallerRef = useRef(false);
  const qualityMonitorStopRef = useRef(null);

  const setMuted = useCallback((next) => {
    mutedRef.current = next;
    setMutedState(next);
  }, []);

  const updateCall = useCallback((next) => {
    callRef.current = typeof next === 'function' ? next(callRef.current) : next;
    setCall(callRef.current);
  }, []);

  const setCamera = useCallback((next) => {
    cameraRef.current = typeof next === 'function' ? next(cameraRef.current) : next;
    setCameraState(cameraRef.current);
  }, []);

  const teardownMedia = useCallback(() => {
    stopCallTone();
    sfuRef.current?.close();
    sfuRef.current = null;
    earlySignalsRef.current = [];
    resumeOnConnectRef.current = false;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (iceRestartDebounceTimerRef.current) {
      clearTimeout(iceRestartDebounceTimerRef.current);
      iceRestartDebounceTimerRef.current = null;
    }
    if (qualityMonitorStopRef.current) {
      qualityMonitorStopRef.current();
      qualityMonitorStopRef.current = null;
    }
    iceRestartingRef.current = false;
    isCallerRef.current = false;

    if (pcRef.current) {
      pcRef.current.ontrack = null;
      pcRef.current.onicecandidate = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.oniceconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    videoSenderRef.current = null;
    nativeVideoRef.current = false;
    pendingCandidatesRef.current = [];
    setRemoteStream(null);
    setMuted(false);
    setCamera(CAMERA_OFF);
    setLocalVideoStream(null);
    setRemoteVideoTrack(null);
    setNativeRemoteVideo(false);
    setRemoteCameraOn(false);
    stopCallAudio();
    setAudioRoute(NO_AUDIO_ROUTES);
    pendingAudioIdRef.current = null;
  }, [setCamera, setMuted]);

  // End locally. With a label, briefly show an "ended" screen before closing.
  // callKitReason: how the iPhone's call log records it.
  const finish = useCallback((endLabel = null, callKitReason = 'remoteEnded') => {
    const wasLive = callRef.current.status !== 'idle';
    teardownMedia();
    if (usesCallKit() && callRef.current.status !== 'ended') {
      endCallKitCall(callRef.current.ckId, callKitReason); // also releases call audio
    }
    clearTimeout(endTimerRef.current);
    if (!wasLive) return;
    if (!endLabel) {
      updateCall(IDLE);
      return;
    }
    playEndTone();
    updateCall((c) => ({ ...c, status: 'ended', endLabel }));
    endTimerRef.current = setTimeout(() => updateCall(IDLE), END_SCREEN_MS);
  }, [teardownMedia, updateCall]);

  const applyAudioState = useCallback((state) => {
    if (['idle', 'ended'].includes(callRef.current.status)) return;
    setAudioRoute((prev) => ({
      // A failed native call returns no devices; keep what we had rather than hiding the control
      devices: state.devices?.length ? state.devices : prev.devices,
      current: pendingAudioIdRef.current ?? state.current ?? prev.current,
    }));
  }, []);

  // Video calls are held at arm's length: use the speaker unless a headset is in
  const preferSpeakerForVideo = useCallback(async (state) => {
    if (cameraRef.current.on && state.current === 'earpiece') return setCallAudioDevice('speaker');
    return state;
  }, []);

  // Mic, plus the camera when wantVideo. Resolves false if the call ended while
  // the permission prompts were up. A denied camera falls back to audio only.
  const getLocalMedia = useCallback(async (wantVideo) => {
    const isLive = () => !['idle', 'ended'].includes(callRef.current.status);
    let video = false;

    if (usesNativeCallMedia()) {
      // iOS: native WebRTC captures itself; only ask for permission here
      await requestNativeMicrophone();
      if (wantVideo) {
        video = await requestNativeCamera().then(() => true, () => false);
      }
      nativeVideoRef.current = video;
    } else {
      let stream;
      if (wantVideo) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: AUDIO_CONSTRAINTS, video: videoConstraints('user'),
          });
          video = true;
        } catch (err) {
          console.warn('Camera unavailable, continuing with audio only:', err?.name);
        }
      }
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: false });
      }
      if (!isLive()) {
        stream.getTracks().forEach((t) => t.stop()); // teardown already ran
        return false;
      }
      localStreamRef.current = stream;
      setLocalVideoStream(video ? new MediaStream(stream.getVideoTracks()) : null);
    }

    if (wantVideo && !video) toast.info('Camera unavailable. Continuing with audio only.');
    setCamera({ on: video, facing: 'user' });

    // Switch to call audio once capture is running, since starting capture can
    // reset the native audio session
    const state = await preferSpeakerForVideo(await startCallAudio());
    if (!isLive()) {
      stopCallAudio();
      return false;
    }
    applyAudioState(state);
    return true;
  }, [applyAudioState, preferSpeakerForVideo, setCamera, toast]);

  const sendSignal = useCallback((data) => {
    const { callId } = callRef.current;
    if (socket && callId) socket.emit('call:signal', { callId, data });
  }, [socket]);

  const sendCameraState = useCallback((on) => {
    const { callId } = callRef.current;
    if (socket && callId) socket.emit('call:media', { callId, camera: on });
  }, [socket]);

  const markConnected = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (iceRestartDebounceTimerRef.current) {
      clearTimeout(iceRestartDebounceTimerRef.current);
      iceRestartDebounceTimerRef.current = null;
    }

    const wasConnecting = callRef.current.status === 'connecting';
    const wasReconnecting = callRef.current.reconnecting;

    if (wasConnecting || wasReconnecting) {
      updateCall((c) => ({
        ...c,
        status: 'active',
        reconnecting: false,
        startedAt: c.startedAt || Date.now(),
      }));
      triggerHaptic('light');
      if (wasConnecting) {
        reportCallConnected(callRef.current.ckId);
        sendCameraState(cameraRef.current.on);
        startCallAudio().then(applyAudioState);
      }
    }

    if (pcRef.current) {
      prioritizeAudioSender(pcRef.current);
      if (!qualityMonitorStopRef.current) {
        qualityMonitorStopRef.current = createCallQualityMonitor(pcRef.current, (quality) => {
          updateCall((c) => (c.networkQuality !== quality ? { ...c, networkQuality: quality } : c));
        });
      }
    }
  }, [applyAudioState, sendCameraState, updateCall]);

  const failCall = useCallback(() => {
    if (socket && callRef.current.callId) socket.emit('call:end', { callId: callRef.current.callId });
    finish(END_REASON_LABELS.failed, 'failed');
  }, [finish, socket]);

  const triggerIceRestart = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || pc.signalingState === 'closed') return;
    if (iceRestartingRef.current) return;
    iceRestartingRef.current = true;

    console.log('[Calls] Triggering ICE restart to recover connection...');
    updateCall((c) => ({ ...c, reconnecting: true, networkQuality: 'poor' }));

    try {
      if (typeof pc.restartIce === 'function') {
        pc.restartIce();
      }
      if (isCallerRef.current) {
        const offer = await pc.createOffer({ iceRestart: true });
        const optimizedOffer = { ...offer, sdp: optimizeAudioSdp(offer.sdp) };
        await pc.setLocalDescription(optimizedOffer);
        sendSignal({ sdp: pc.localDescription, iceRestart: true });
        prioritizeAudioSender(pc);
      } else {
        sendSignal({ requestIceRestart: true });
      }
    } catch (err) {
      console.warn('[Calls] ICE restart error:', err);
    } finally {
      setTimeout(() => {
        iceRestartingRef.current = false;
      }, 3500);
    }
  }, [sendSignal, updateCall]);

  const onMediaDisconnected = useCallback(() => {
    console.warn('[Calls] Media disconnected. Entering reconnecting state...');
    updateCall((c) => ({ ...c, reconnecting: true, networkQuality: 'poor' }));

    if (!reconnectTimerRef.current) {
      reconnectTimerRef.current = setTimeout(() => {
        console.warn('[Calls] Reconnection grace period (30s) expired. Failing call.');
        failCall();
      }, RECONNECT_GRACE_MS);
    }

    if (!iceRestartDebounceTimerRef.current) {
      iceRestartDebounceTimerRef.current = setTimeout(() => {
        iceRestartDebounceTimerRef.current = null;
        if (callRef.current.reconnecting && pcRef.current?.connectionState !== 'connected') {
          triggerIceRestart();
        }
      }, RESTART_ICE_DEBOUNCE_MS);
    }
  }, [failCall, triggerIceRestart, updateCall]);

  const onMediaFailed = useCallback(() => {
    console.warn('[Calls] Media connection failed. Triggering immediate ICE restart...');
    updateCall((c) => ({ ...c, reconnecting: true, networkQuality: 'poor' }));

    if (!reconnectTimerRef.current) {
      reconnectTimerRef.current = setTimeout(() => {
        console.warn('[Calls] Recovery window (30s) expired after failure. Failing call.');
        failCall();
      }, RECONNECT_GRACE_MS);
    }

    triggerIceRestart();
  }, [failCall, triggerIceRestart, updateCall]);

  // Peer connection with our tracks, for either media mode
  const createMediaPeer = useCallback((rtcConfig) => {
    const pc = newPeer(rtcConfig, localStreamRef.current, nativeVideoRef.current);
    pcRef.current = pc;
    if (!usesNativeCallMedia()) {
      videoSenderRef.current = pc.getSenders().find((s) => s.track?.kind === 'video') || null;
    }
    return pc;
  }, []);

  const handleRemoteTrack = useCallback((e) => {
    if (e.streams?.[0]) setRemoteStream(e.streams[0]); // played by the overlay's <audio>
    if (e.track.kind === 'video') {
      const { track } = e;
      setRemoteVideoTrack(track);
      track.addEventListener('ended', () => setRemoteVideoTrack((t) => (t === track ? null : t)));
    }
  }, []);

  // ── SFU mode ────────────────────────────────────────────────────
  const startSfu = useCallback((grant) => {
    updateCall((c) => ({ ...c, media: 'sfu' }));
    const session = new SfuSession({
      url: grant.url,
      token: grant.token,
      iceServers: grant.iceServers,
      createPeer: createMediaPeer,
      onTrack: handleRemoteTrack,
      onConnected: markConnected,
      onFailed: (reason) => {
        console.warn('SFU session failed:', reason);
        failCall();
      },
      onClosed: () => finish('Call ended'),
    });
    sfuRef.current = session;
    session.start();
  }, [createMediaPeer, failCall, finish, handleRemoteTrack, markConnected, updateCall]);

  // ── P2P mode ────────────────────────────────────────────────────
  const createPeerConnection = useCallback(() => {
    updateCall((c) => ({ ...c, media: 'p2p' }));
    const pc = createMediaPeer(buildRtcConfig());

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal({ candidate: e.candidate });
    };
    pc.ontrack = handleRemoteTrack;
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') markConnected();
      else if (s === 'disconnected') onMediaDisconnected();
      else if (s === 'failed') onMediaFailed();
    };
    // Older WebKit only reports ICE state
    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      if (['connected', 'completed'].includes(s)) markConnected();
      else if (s === 'disconnected') onMediaDisconnected();
      else if (s === 'failed') onMediaFailed();
    };
    return pc;
  }, [createMediaPeer, handleRemoteTrack, markConnected, onMediaDisconnected, onMediaFailed, sendSignal, updateCall]);

  const flushCandidates = async (pc) => {
    const queued = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    for (const c of queued) {
      try { await pc.addIceCandidate(c); } catch (e) { console.warn('ICE candidate error', e); }
    }
  };

  // Apply a relayed P2P SDP / ICE message
  const applySignal = useCallback(async (data) => {
    const pc = pcRef.current;
    if (!pc) return;
    try {
      if (data.requestIceRestart && isCallerRef.current) {
        triggerIceRestart();
        return;
      }
      if (data.sdp) {
        const remoteSdp = { ...data.sdp, sdp: optimizeAudioSdp(data.sdp.sdp) };
        await pc.setRemoteDescription(remoteSdp);
        if (data.sdp.type === 'offer') {
          const answer = await pc.createAnswer();
          const localAnswer = { ...answer, sdp: optimizeAudioSdp(answer.sdp) };
          await pc.setLocalDescription(localAnswer);
          sendSignal({ sdp: pc.localDescription });
          prioritizeAudioSender(pc);
        }
        await flushCandidates(pc);
      } else if (data.candidate) {
        if (pc.remoteDescription) await pc.addIceCandidate(data.candidate);
        else pendingCandidatesRef.current.push(data.candidate);
      }
    } catch (err) {
      console.error('Call signaling error:', err);
    }
  }, [sendSignal, triggerIceRestart]);

  // ── Caller ──────────────────────────────────────────────────────
  const startCall = useCallback(async (peer, { video = false, ckId: existingCkId = null } = {}) => {
    if (!peer?.id || callRef.current.status !== 'idle') return;
    unlockCallAudio();
    clearTimeout(endTimerRef.current);
    hangupBeforeAckRef.current = false;
    isCallerRef.current = true;
    updateCall({ ...IDLE, status: 'outgoing', peer, video });
    setRemoteCameraOn(video);

    let ckId = existingCkId;
    if (usesCallKit()) {
      if (!ckId) {
        // Normal call from inside the app: register with CallKit
        ckId = await startCallKitCall({ name: peer.username, handle: callKitHandle(peer), video });
      }
      if (callRef.current.status !== 'outgoing') {
        endCallKitCall(ckId); // hung up in the meantime
        return;
      }
      updateCall((c) => ({ ...c, ckId }));
    }

    try {
      if (!(await getLocalMedia(video))) return; // hung up while prompting
    } catch (err) {
      console.error('Microphone error:', err);
      finish(END_REASON_LABELS.mic_denied);
      return;
    }
    if (callRef.current.status !== 'outgoing') return;

    playRingback();

    // Ensure socket is connected (especially on cold launch from Call History)
    const activeSocket = socketRef.current?.connected ? socketRef.current : await waitForSocket();
    if (!activeSocket) {
      finish(END_REASON_LABELS.failed);
      toast.error('Calling is not available right now.');
      return;
    }

    const invite = { calleeId: peer.id, caps: CALL_CAPS, video };
    // Time out if the server never acknowledges (e.g. it doesn't support calls yet)
    activeSocket.timeout(10000).emit('call:invite', invite, (timeoutErr, res = {}) => {
      if (timeoutErr) {
        if (callRef.current.status === 'outgoing' && !callRef.current.callId) {
          finish(END_REASON_LABELS.failed);
          toast.error('Calling is not available right now.');
        }
        return;
      }
      if (res.error) {
        if (res.error === 'not_allowed') {
          finish();
          toast.info('You can call someone once they have messaged you.');
        } else if (res.error === 'already_in_call') {
          finish();
          toast.info('You are already in a call.');
        } else {
          finish(END_REASON_LABELS[res.error] || END_REASON_LABELS.failed);
        }
        return;
      }
      if (hangupBeforeAckRef.current) {
        activeSocket.emit('call:end', { callId: res.callId });
        return;
      }
      updateCall((c) => ({ ...c, callId: res.callId, peer: { ...c.peer, ...res.callee } }));
    });
  }, [finish, getLocalMedia, toast, updateCall, waitForSocket]);

  // ── Callee ──────────────────────────────────────────────────────
  // withVideo: answer a video call with our camera on (false = audio only)
  const acceptCall = useCallback(async ({ withVideo } = {}) => {
    const { callId, status, video } = callRef.current;
    if (!socket || status !== 'incoming') return;
    isCallerRef.current = false;
    stopCallTone();
    triggerHaptic('medium');
    // Answering through CallKit is what activates the call audio
    if (usesCallKit()) answerCallKitCall(callRef.current.ckId);
    // Show "Connecting…" immediately: the mic/camera prompt and the audio
    // session setup below can take a moment, and an unchanged screen makes
    // people tap again (and again, onto whatever button replaces Accept).
    // Leaving 'incoming' also turns any further taps on Accept into no-ops.
    updateCall((c) => ({ ...c, status: 'connecting', answeredAt: Date.now() }));
    try {
      if (!(await getLocalMedia(withVideo ?? video))) return; // caller hung up during the prompt
    } catch (err) {
      console.error('Microphone error:', err);
      socket.emit('call:decline', { callId, reason: 'mic_denied' });
      finish(END_REASON_LABELS.mic_denied);
      return;
    }
    // The server answers with how media will flow: through the SFU (with our
    // join grant) or peer-to-peer. A server that never acks means P2P.
    socket.timeout(8000).emit('call:accept', { callId, caps: CALL_CAPS }, async (timeoutErr, res = {}) => {
      if (callRef.current.callId !== callId || callRef.current.status !== 'connecting') return;
      if (!timeoutErr && res.error) {
        finish(); // the caller hung up first
        return;
      }
      if (timeoutErr && !socket.connected) {
        failCall(); // lost the socket, so we can't tell which mode the caller is in
        return;
      }
      if (!timeoutErr && res.media === 'sfu' && res.sfu) {
        startSfu(res.sfu);
        return;
      }
      createPeerConnection();
      const early = earlySignalsRef.current;
      earlySignalsRef.current = [];
      for (const data of early) await applySignal(data);
    });
  }, [applySignal, createPeerConnection, failCall, finish, getLocalMedia, socket, startSfu, updateCall]);

  const declineCall = useCallback(() => {
    const { callId } = callRef.current;
    if (socket && callId) socket.emit('call:decline', { callId, reason: 'user_declined_ui' });
    finish();
  }, [finish, socket]);

  const hangUp = useCallback(() => {
    const { callId } = callRef.current;
    if (socket && callId) socket.emit('call:end', { callId });
    else hangupBeforeAckRef.current = true;
    finish(callRef.current.status === 'outgoing' ? null : 'Call ended');
  }, [finish, socket]);

  const selectAudioOutput = useCallback(async (id) => {
    if (!id) return;
    // Show the choice immediately; the native route switch completes asynchronously
    pendingAudioIdRef.current = id;
    setAudioRoute((prev) => ({ ...prev, current: id }));
    applyAudioState(await setCallAudioDevice(id));
    setTimeout(async () => {
      if (pendingAudioIdRef.current !== id) return; // superseded by another tap
      pendingAudioIdRef.current = null;
      applyAudioState(await getCallAudioState()); // confirm the real route
    }, 700);
  }, [applyAudioState]);

  // Headset connected/disconnected mid-call
  useEffect(() => onCallAudioChange(applyAudioState), [applyAudioState]);

  const applyMuted = useCallback((next) => {
    if (usesNativeCallMedia()) {
      setNativeMuted(next).catch((err) => console.warn('Mute failed', err));
      setMuted(next);
      return;
    }
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !next;
    setMuted(next);
  }, [setMuted]);

  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    applyMuted(next);
    setCallKitMuted(callRef.current.ckId, next); // keep the system call screen in sync
  }, [applyMuted]);

  // ── Camera ──────────────────────────────────────────────────────

  // Web: swap our outgoing video track (null = camera off, which also turns
  // the camera light off). Adding video to a voice call needs a renegotiation,
  // which only the SFU path supports.
  const replaceLocalVideo = useCallback(async (track) => {
    const stream = localStreamRef.current;
    if (!stream) return false;
    const old = stream.getVideoTracks()[0];
    if (videoSenderRef.current) {
      await videoSenderRef.current.replaceTrack(track);
      if (track) stream.addTrack(track);
    } else if (track && sfuRef.current) {
      stream.addTrack(track);
      videoSenderRef.current = sfuRef.current.publishTrack(track, stream) || null;
    } else if (track) {
      return false;
    }
    if (old) {
      stream.removeTrack(old);
      old.stop();
    }
    setLocalVideoStream(track ? new MediaStream([track]) : null);
    return true;
  }, []);

  const toggleCamera = useCallback(async () => {
    const { on, facing } = cameraRef.current;
    const next = !on;
    try {
      if (usesNativeCallMedia()) {
        if (next && !nativeVideoRef.current) {
          // Voice call → video: add the camera natively, then renegotiate
          if (!sfuRef.current) return;
          await requestNativeCamera();
          await startNativeVideo();
          nativeVideoRef.current = true;
          sfuRef.current.renegotiate();
        } else {
          await setNativeCameraEnabled(next);
        }
      } else if (next) {
        const track = await getCameraTrack(facing);
        if (!(await replaceLocalVideo(track))) {
          track.stop();
          return;
        }
      } else {
        await replaceLocalVideo(null);
      }
    } catch (err) {
      console.warn('Camera toggle failed:', err);
      if (next) toast.error('Could not turn on the camera.');
      return;
    }
    setCamera((c) => ({ ...c, on: next }));
    sendCameraState(next);
    if (next) {
      const state = await preferSpeakerForVideo(await getCallAudioState());
      applyAudioState(state);
    }
  }, [applyAudioState, preferSpeakerForVideo, replaceLocalVideo, sendCameraState, setCamera, toast]);

  const flipCamera = useCallback(async () => {
    const { on, facing } = cameraRef.current;
    if (!on) return;
    try {
      if (usesNativeCallMedia()) {
        const res = await switchNativeCamera();
        setCamera((c) => ({ ...c, facing: res?.facing || (facing === 'user' ? 'environment' : 'user') }));
        return;
      }
      const nextFacing = facing === 'user' ? 'environment' : 'user';
      const track = await getCameraTrack(nextFacing);
      if (await replaceLocalVideo(track)) setCamera((c) => ({ ...c, facing: nextFacing }));
      else track.stop();
    } catch (err) {
      console.warn('Camera flip failed:', err);
    }
  }, [replaceLocalVideo, setCamera]);

  // iOS: native tells us when the other side's video track comes and goes
  useEffect(() => {
    if (!usesNativeCallMedia()) return undefined;
    return onNativeRemoteVideo(setNativeRemoteVideo);
  }, []);

  // ── Socket events ───────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return undefined;

    const onIncoming = ({ callId, caller, video }) => {
      if (callRef.current.status !== 'idle' && callRef.current.status !== 'ended') return;
      clearTimeout(endTimerRef.current);
      updateCall({
        ...IDLE, status: 'incoming', callId, ckId: usesCallKit() ? callId : null, peer: caller, video: !!video,
      });
      setRemoteCameraOn(!!video);

      // Already answered on the lock screen before the app caught up
      if (pendingCallKitAnswerRef.current === callId) {
        pendingCallKitAnswerRef.current = null;
        acceptCallRef.current();
        return;
      }

      if (usesCallKit()) {
        // The system rings (usually already, from the VoIP push). Ring in the
        // app only if iOS won't show the call.
        reportIncomingCall({ id: callId, name: caller.username, handle: callKitHandle(caller), video })
          .then((reported) => {
            const c = callRef.current;
            if (!reported && c.callId === callId && c.status === 'incoming') playRingtone();
          });
        return;
      }
      playRingtone();
      // In the apps the native ringtone vibrates per the ringer setting
      if (!Capacitor.isNativePlatform()) triggerHaptic('heavy');
    };

    const onAccepted = async ({ callId, media, sfu }) => {
      if (callRef.current.callId !== callId || callRef.current.status !== 'outgoing') return;
      stopCallTone();
      updateCall((c) => ({ ...c, status: 'connecting' }));
      reportCallConnecting(callRef.current.ckId);
      if (media === 'sfu' && sfu) {
        startSfu(sfu);
        return;
      }
      const pc = createPeerConnection();
      try {
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: callRef.current.video,
        });
        const optimizedOffer = { ...offer, sdp: optimizeAudioSdp(offer.sdp) };
        await pc.setLocalDescription(optimizedOffer);
        sendSignal({ sdp: pc.localDescription });
        prioritizeAudioSender(pc);
      } catch (err) {
        console.error('Offer error:', err);
        socket.emit('call:end', { callId });
        finish(END_REASON_LABELS.failed);
      }
    };

    const onSignal = ({ callId, data }) => {
      if (callRef.current.callId !== callId || sfuRef.current || !data) return;
      // Callee still waiting for its accept ack to learn the media mode
      if (!pcRef.current) earlySignalsRef.current.push(data);
      else applySignal(data);
    };

    const onMedia = ({ callId, camera: on }) => {
      if (callRef.current.callId === callId) setRemoteCameraOn(!!on);
    };

    const onEnded = ({ callId, reason }) => {
      if (callRef.current.callId !== callId) return;
      const { status } = callRef.current;
      const callKitReason = callKitEndReason(reason, status);
      if (status === 'incoming' || reason === 'answered_elsewhere') {
        finish(null, callKitReason); // missed / cancelled / picked up on another device
        return;
      }
      finish(END_REASON_LABELS[reason] || 'Call ended', callKitReason);
    };

    // A ringing call ends server-side when the socket drops. A connected call is
    // kept alive: media may still flow over WebRTC UDP, and the socket reconnects
    // within seconds of a network switch.
    const onDisconnect = () => {
      const { status, callId } = callRef.current;
      if (status === 'idle' || status === 'ended') return;
      if (callId && (status === 'connecting' || status === 'active')) {
        console.log('[Calls] Socket disconnected mid-call. Keeping call alive in reconnecting mode...');
        resumeOnConnectRef.current = true;
        updateCall((c) => ({ ...c, reconnecting: true }));
        return;
      }
      finish(status === 'incoming' ? null : 'Call ended');
    };

    const attemptResume = () => {
      const { callId, status } = callRef.current;
      if (!callId || (status !== 'connecting' && status !== 'active')) return;
      if (!socket.connected) return;

      socket.timeout(10000).emit('call:resume', { callId }, (timeoutErr, res = {}) => {
        if (callRef.current.callId !== callId) return;
        if (timeoutErr) {
          console.warn('[Calls] call:resume timed out. Retrying in 3s without dropping call...');
          setTimeout(attemptResume, 3000);
          return;
        }
        if (res.error === 'ended') {
          finish('Call ended');
          return;
        }
        resumeOnConnectRef.current = false;
        sendCameraState(cameraRef.current.on);
        if (pcRef.current?.connectionState === 'disconnected' || pcRef.current?.connectionState === 'failed') {
          triggerIceRestart();
        }
      });
    };

    const onConnect = () => {
      if (!resumeOnConnectRef.current) return;
      attemptResume();
    };

    socket.on('call:incoming', onIncoming);
    socket.on('disconnect', onDisconnect);
    socket.on('connect', onConnect);
    socket.on('call:accepted', onAccepted);
    socket.on('call:signal', onSignal);
    socket.on('call:media', onMedia);
    socket.on('call:ended', onEnded);
    return () => {
      socket.off('call:incoming', onIncoming);
      socket.off('disconnect', onDisconnect);
      socket.off('connect', onConnect);
      socket.off('call:accepted', onAccepted);
      socket.off('call:signal', onSignal);
      socket.off('call:media', onMedia);
      socket.off('call:ended', onEnded);
    };
  }, [applySignal, createPeerConnection, finish, sendCameraState, sendSignal, socket, startSfu, triggerIceRestart, updateCall]);

  // ── iPhone system call screen (CallKit) ─────────────────────────
  acceptCallRef.current = acceptCall;
  declineCallRef.current = declineCall;
  hangUpRef.current = hangUp;
  startCallRef.current = startCall;

  useEffect(() => {
    if (!usesCallKit()) return undefined;
    const isCurrent = (id) => {
      const { ckId, callId, status } = callRef.current;
      return !!id && (ckId === id || callId === id) && status !== 'idle' && status !== 'ended';
    };

    // A call the VoIP push showed that the app doesn't know yet: restore call state
    // from the server and accept it immediately if already answered on CallKit
    const checkUnknownCall = async (id) => {
      const activeSocket = socketRef.current?.connected ? socketRef.current : await waitForSocket(5000);
      if (!activeSocket) return;
      activeSocket.timeout(10000).emit('call:status', { callId: id }, (timeoutErr, res = {}) => {
        if (timeoutErr) return;
        if (res.state === 'ringing' && res.caller) {
          clearTimeout(endTimerRef.current);
          updateCall({
            ...IDLE,
            status: 'incoming',
            callId: id,
            ckId: usesCallKit() ? id : null,
            peer: res.caller,
            video: !!res.video,
          });
          setRemoteCameraOn(!!res.video);
          if (pendingCallKitAnswerRef.current === id) {
            pendingCallKitAnswerRef.current = null;
            acceptCallRef.current?.();
          }
          return;
        }
        if (callRef.current.ckId === id) return;
        if (pendingCallKitAnswerRef.current === id) pendingCallKitAnswerRef.current = null;
        endCallKitCall(id, res.state === 'active' ? 'answeredElsewhere' : 'unanswered');
      });
    };

    const offs = [
      onCallKitEvent('incoming', ({ id }) => {
        if (!isCurrent(id)) checkUnknownCall(id);
      }),
      onCallKitEvent('answer', ({ id }) => {
        if (isCurrent(id)) {
          acceptCallRef.current?.(); // no-op when answered in the app
          return;
        }
        pendingCallKitAnswerRef.current = id;
        checkUnknownCall(id);
      }),
      onCallKitEvent('end', ({ id, answered, outgoing }) => {
        if (isCurrent(id)) {
          if (callRef.current.status === 'incoming') {
            // When CallKit dismisses or fails (e.g. background restriction or lock-screen issue),
            // do NOT emit call:decline to the server! Emitting call:decline kills legitimately ringing calls
            // on the user's other devices (e.g. Android phone). Just dismiss locally.
            // If the user explicitly wants to decline, they can tap Decline in the app UI.
            finish();
            return;
          }
          hangUpRef.current();
          return;
        }
        if (pendingCallKitAnswerRef.current === id) pendingCallKitAnswerRef.current = null;
        // If the call is not current, do NOT emit call:decline to the server.
        // Doing so kills legitimately ringing calls on the user's other devices (e.g. Android phone).
      }),
      onCallKitEvent('mute', ({ id, muted: next }) => {
        if (isCurrent(id) && next !== mutedRef.current) applyMuted(next);
      }),
      onCallKitEvent('startCall', async ({ id, handle, name, video }) => {
        if (callRef.current.status !== 'idle') {
          if (callRef.current.ckId !== id) {
            endCallKitCall(id, 'failed');
          }
          return;
        }

        const candidates = [handle, name]
          .filter(Boolean)
          .map((s) => s.replace(/^@+/, '').trim())
          .filter(Boolean);

        if (candidates.length === 0) {
          endCallKitCall(id, 'failed');
          return;
        }

        let user = null;
        for (const target of candidates) {
          try {
            const res = await axios.get(`/api/users/${encodeURIComponent(target)}`);
            if (res.data?.id) {
              user = res.data;
              break;
            }
          } catch (_) {}
        }

        if (!user || !user.id) {
          console.warn('[CallKit] Could not find user for startCall:', { handle, name });
          endCallKitCall(id, 'failed');
          toast.error('User not found.');
          return;
        }

        const peer = {
          id: user.id,
          username: user.username,
          handle: user.handle,
          profilePicture: user.profile_picture || user.profilePicture || null,
        };

        startCallRef.current?.(peer, { video: !!video, ckId: id });
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [applyMuted, toast, updateCall, waitForSocket]);

  // Only terminate if the socket is permanently gone (e.g. logout),
  // with a 15-second grace window to tolerate React re-renders or token refreshes.
  useEffect(() => {
    if (!socket && callRef.current.status !== 'idle' && callRef.current.status !== 'ended') {
      const timer = setTimeout(() => {
        if (!socketRef.current?.connected && callRef.current.status !== 'idle') {
          finish('Call ended');
        }
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [socket, finish]);

  useEffect(() => () => {
    clearTimeout(endTimerRef.current);
    teardownMedia();
  }, [teardownMedia]);

  const hasRemoteVideo = remoteCameraOn && (usesNativeCallMedia() ? nativeRemoteVideo : !!remoteVideoTrack);

  const value = {
    call,
    muted,
    remoteStream,
    isInCall: call.status !== 'idle' && call.status !== 'ended',
    startCall,
    acceptCall,
    declineCall,
    hangUp,
    toggleMute,
    audioRoute,
    selectAudioOutput,
    // Video
    camera,
    localVideoStream,
    remoteVideoTrack,
    hasRemoteVideo,
    // Show the video layout: a video call, or either camera turned on mid-call
    isVideo: call.video || camera.on || hasRemoteVideo,
    // P2P can't add a camera to a voice call (it would need renegotiation)
    canUseCamera: call.video || call.media === 'sfu',
    canFlipCamera: Capacitor.isNativePlatform(),
    toggleCamera,
    flipCamera,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};

export default CallProvider;
